import { API_URL } from "../../helpers/constants";
import { getToken } from "../../helpers/tokenStorage";

/**
 * Thin fetch wrappers around the endpoints /access/members needs.
 * Follows the direct-fetch style rather than going through actions.js —
 * codebase accepts either.
 *
 * API_URL already includes the /api suffix per feedback_console_api_url.
 * Never write `${API_URL}/api/...`.
 */

const authHeaders = () => {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}`, "Content-Type": "application/json" }
           : { "Content-Type": "application/json" };
};

async function jsonOrThrow(res) {
  if (res.ok) return res.text().then((t) => (t ? JSON.parse(t) : null));
  const body = await res.text().catch(() => "");
  const err = new Error(`HTTP ${res.status}: ${body.slice(0, 200)}`);
  err.status = res.status;
  err.body = body;
  throw err;
}

export function getMe() {
  return fetch(`${API_URL}/accounts/me`, { headers: authHeaders() }).then(jsonOrThrow);
}

/**
 * Fetch the caller's own account-member row for the given accountId.
 * Coord surfaces this as GET /accounts/{id}/members/me so non-admins
 * can read their own role + canCreateApplications flag + per-project
 * grants without needing the admin-only members list.
 *
 * If the endpoint returns 404 (older coord builds where this route is
 * not deployed yet) or 403, we resolve to null — callers treat that as
 * "no delegated grants known, hide the CTA" per the gate contract in
 * helpers/appPermissions.js.
 */
export function getMyMember(accountId) {
  if (!accountId) return Promise.resolve(null);
  return fetch(`${API_URL}/accounts/${accountId}/members/me`, {
    headers: authHeaders(),
  })
    .then(jsonOrThrow)
    .catch((e) => {
      if (e && (e.status === 404 || e.status === 403)) return null;
      throw e;
    });
}

export function getAccountMembers(accountId) {
  return fetch(`${API_URL}/accounts/${accountId}/members`, {
    headers: authHeaders(),
  }).then(jsonOrThrow);
}

export function getProjects() {
  return fetch(`${API_URL}/k8s-project/`, { headers: authHeaders() })
    .then(jsonOrThrow)
    .then((data) => (Array.isArray(data) ? data : []));
}

/**
 * Invite (or directly add) a member on the account.
 * body: { email, role, canCreateProjects, projectGrants? }
 * projectGrants is [{ projectId, role }].
 */
export function inviteMember(accountId, body) {
  return fetch(`${API_URL}/accounts/${accountId}/members`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(body),
  }).then(jsonOrThrow);
}

/**
 * Invite a user directly into a single workspace (project).
 *
 * SCK-476: coord requires the POST to hit the project's OWNING account,
 * not the caller's account (SCK-475 fix on the CLI side). Callers pass
 * the project payload so we can read `project.accountId` off it.
 *
 * body sent to coord:
 *   { email, role: "MEMBER", canCreateProjects: false,
 *     projectGrants: [{ projectId, role }] }
 *
 * `role` in the grant is the PROJECT role (VIEWER / CONTRIBUTOR /
 * PROJECT_ADMIN). Account role stays MEMBER because we do not want
 * project-scoped invites to accidentally promote a caller to admin.
 */
export function inviteToProject({ project, email, projectRole }) {
  if (!project?.accountId) {
    return Promise.reject(
      Object.assign(new Error("Workspace is missing an owning account id."), {
        status: 400,
      })
    );
  }
  return fetch(`${API_URL}/accounts/${project.accountId}/members`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({
      email,
      role: "MEMBER",
      canCreateProjects: false,
      projectGrants: [{ projectId: project.id, role: projectRole }],
    }),
  }).then(jsonOrThrow);
}

/**
 * Update an existing member's account role / create-projects grant.
 * body: { role, canCreateProjects }
 */
export function updateMember(accountId, userId, body) {
  return fetch(`${API_URL}/accounts/${accountId}/members/${userId}`, {
    method: "PUT",
    headers: authHeaders(),
    body: JSON.stringify(body),
  }).then(jsonOrThrow);
}

/**
 * Remove a member from the account.
 */
export function removeMember(accountId, userId) {
  return fetch(`${API_URL}/accounts/${accountId}/members/${userId}`, {
    method: "DELETE",
    headers: authHeaders(),
  }).then(jsonOrThrow);
}

/**
 * SCK-618 — list invitations scoped to a given kind + owning account.
 *
 * Backs the /access/invitations lifecycle table. Hits coord's paginated
 * v1 listing endpoint. Coord already accepts `kind` server-side; the
 * `targetAccountId` filter is passed through so once coord narrows the
 * page to that account we get the exact rows the ACCOUNT_ADMIN caller is
 * allowed to see. Until the query-param filter lands upstream we still
 * receive the account's rows because ACCOUNT_ADMIN callers only ever
 * have visibility to their own account's invitations.
 *
 * Returns Page<InvitationRecord> shape: `{ content: [...], totalElements,
 * ... }`. Callers should read `.content` and treat `content` missing as
 * an empty list (dev seeds sometimes hand back a bare array).
 */
export function listInvitations({
  kind = "ACCOUNT_MEMBER",
  targetAccountId,
  status,
  page = 0,
  size = 100,
} = {}) {
  const params = new URLSearchParams();
  params.set("kind", kind);
  if (targetAccountId) params.set("targetAccountId", targetAccountId);
  if (status) params.set("status", status);
  params.set("page", String(page));
  params.set("size", String(size));
  return fetch(`${API_URL}/v1/invitations?${params.toString()}`, {
    headers: authHeaders(),
  }).then(jsonOrThrow);
}

/**
 * SCK-618 — revoke an outstanding invitation so its token can never
 * redeem. Idempotent-ish server-side: revoking a REDEEMED or EXPIRED
 * invitation returns 4xx which we surface to the caller.
 */
export function revokeInvitation(invitationId) {
  return fetch(`${API_URL}/v1/invitations/${invitationId}/revoke`, {
    method: "POST",
    headers: authHeaders(),
  }).then(jsonOrThrow);
}

// -------------- SCK-631 — Customer-Managed Keys (BYOK) --------------

/**
 * Probe a customer KEK ARN end-to-end. Coord runs DescribeKey +
 * GenerateDataKey + Decrypt against the customer's KMS grant and
 * returns a structured result — 200 with `ok:true` + fingerprint on
 * success, or 200 with `ok:false` + error bucket on refusal. The page
 * relies on this being a structured 200 (never a 500) so the failure
 * modal renders as a first-class UX state.
 */
export function verifyKmsKey({ provider, kekArn }) {
  return fetch(`${API_URL}/account/kms/verify`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ provider, kekArn }),
  }).then(jsonOrThrow);
}

// ---------------- SCK-569 — support audit ----------------

/**
 * SCK-569 — list support-access grants scoped to the caller's account.
 * Filters map 1:1 to the coord contract; every one is optional. Status
 * takes the derived effective enum (Active | Completed | Denied |
 * Expired) — coord folds the raw APPROVED / DENIED / EXPIRED and the
 * session's revoked_at + expires_at into that single value so the
 * console never has to know the join shape.
 */
export function listSupportAudit({
  accountId,
  engineerId,
  status,
  pageStart,
  pageEnd,
} = {}) {
  const params = new URLSearchParams();
  if (accountId) params.set("accountId", accountId);
  if (engineerId) params.set("engineerId", engineerId);
  if (status && status !== "All") params.set("status", status);
  if (pageStart) params.set("pageStart", pageStart);
  if (pageEnd) params.set("pageEnd", pageEnd);
  const qs = params.toString();
  return fetch(
    `${API_URL}/support-audit${qs ? `?${qs}` : ""}`,
    { headers: authHeaders() }
  ).then(jsonOrThrow);
}

/**
 * SCK-569 — drill-down: every SCK-567 action stamped inside a specific
 * support-access session. Returns [] until the Loki-mirror slice lands
 * on coord; the console renders "no recorded actions yet" then.
 */
export function listSupportAuditActions(sessionId, { accountId } = {}) {
  const params = new URLSearchParams();
  if (accountId) params.set("accountId", accountId);
  const qs = params.toString();
  return fetch(
    `${API_URL}/support-audit/${sessionId}/actions${qs ? `?${qs}` : ""}`,
    { headers: authHeaders() }
  ).then(jsonOrThrow);
}

/**
 * SCK-569 — set the caller's own "notify me on new support access"
 * preference. Persisted in users.preferences_json (same envelope
 * MePreferencesController round-trips), so the toggle survives login.
 */
export function putSupportAuditSubscribe(onNewSession) {
  return fetch(`${API_URL}/support-audit/subscribe`, {
    method: "PUT",
    headers: authHeaders(),
    body: JSON.stringify({ onNewSession: !!onNewSession }),
  }).then(jsonOrThrow);
}

/**
 * Persist a customer KEK binding. Coord re-runs verify inline so a
 * forged body cannot slip past the green fingerprint the operator saw
 * by hand — the "Bind" button should never be clickable until a Verify
 * has come back OK, and the server-side re-verify is defense-in-depth.
 */
export function bindKmsKey({ provider, kekArn, label }) {
  return fetch(`${API_URL}/account/kms/bind`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ provider, kekArn, label: label || null }),
  }).then(jsonOrThrow);
}

export function listKmsBindings() {
  return fetch(`${API_URL}/account/kms`, {
    headers: authHeaders(),
  })
    .then(jsonOrThrow)
    .then((data) => (Array.isArray(data) ? data : []));
}

/**
 * Flip a binding to REVOKED. Idempotent on an already-revoked row —
 * a double-click will not 409.
 */
export function revokeKmsBinding(bindingId) {
  return fetch(`${API_URL}/account/kms/${bindingId}/revoke`, {
    method: "POST",
    headers: authHeaders(),
  }).then(jsonOrThrow);
}

/**
 * SCK-573 slice B — kick off a per-account plaintext-secrets migration.
 * The endpoint is SYSTEM-only in coord today; the console still surfaces
 * the button because the page will eventually run under a support-
 * session (SCK-568) with SYSTEM equivalence. On a non-SYSTEM caller the
 * request will 403 and the page reports "not permitted".
 */
export function migrateDaariSecrets({ accountId, customerKmsKey, dryRun = false }) {
  const qs = dryRun ? "?dryRun=true" : "";
  return fetch(`${API_URL}/daari/secrets/migrate${qs}`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ accountId, customerKmsKey }),
  }).then(jsonOrThrow);
}

// ---------------- SCK-549 — support MCP token mint ----------------

/**
 * SCK-549 PR-6 — the four caller-scoped Support MCP token endpoints.
 *
 * Tokens are minted per-user (JWT propagated by `authHeaders()`), scoped
 * to `/api/v1/mcp/*` only, and TTL-capped at 90 days on coord. The mint
 * response is the ONLY moment the raw token is available — every list
 * projection returns `{ prefix, last4, ... }` and never the full value.
 *
 * The list endpoint mirrors the coord `X-Token-Expires-In-Days` response
 * header on the SMALLEST time-to-expiry across the caller's tokens so
 * the page can render a rotation banner ≥ 15 days out. The helper
 * returns both the list body and the header value in a single envelope.
 */

/**
 * POST /v1/support/mcp/tokens — mint one new token. Body: { name }.
 * Response includes `token` (raw value, shown once), plus the same
 * masked fields the list uses (id, name, prefix, last4, createdAt,
 * expiresAt, lastUsedAt). Callers MUST NOT log the raw token.
 */
export function mintSupportMcpToken({ name }) {
  return fetch(`${API_URL}/v1/support/mcp/tokens`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ name }),
  }).then(jsonOrThrow);
}

/**
 * GET /v1/support/mcp/tokens — list the caller's own tokens (masked).
 * Returns `{ tokens: [...], expiresInDays: number|null }` — the second
 * field is parsed from the coord-side `X-Token-Expires-In-Days` header.
 * `expiresInDays` is null when the caller has no live tokens (nothing
 * to warn about) or the header is absent (older coord builds).
 */
export function listSupportMcpTokens() {
  return fetch(`${API_URL}/v1/support/mcp/tokens`, {
    headers: authHeaders(),
  }).then(async (res) => {
    if (!res.ok) {
      const body = await res.text().catch(() => "");
      const err = new Error(`HTTP ${res.status}: ${body.slice(0, 200)}`);
      err.status = res.status;
      err.body = body;
      throw err;
    }
    const headerRaw = res.headers.get("X-Token-Expires-In-Days");
    const parsed = headerRaw == null ? null : Number(headerRaw);
    const expiresInDays = Number.isFinite(parsed) ? parsed : null;
    const text = await res.text();
    const body = text ? JSON.parse(text) : null;
    const tokens = Array.isArray(body)
      ? body
      : Array.isArray(body?.tokens)
        ? body.tokens
        : [];
    return { tokens, expiresInDays };
  });
}

/**
 * DELETE /v1/support/mcp/tokens/{id} — revoke a single token. Idempotent
 * on the coord side; a double-click will not 409. Never sends the raw
 * token value — the id alone is enough.
 */
export function revokeSupportMcpToken(id) {
  return fetch(`${API_URL}/v1/support/mcp/tokens/${id}`, {
    method: "DELETE",
    headers: authHeaders(),
  }).then(jsonOrThrow);
}

/**
 * SCK-569 — SOC 2 evidence CSV export. Same filters as the list; the
 * server returns text/csv with an attachment Content-Disposition. This
 * helper resolves to the raw CSV text so the caller can hand it to
 * URL.createObjectURL for a download link (a plain <a href> would let
 * the fetch's Authorization header slip through the trip, which is
 * why we fetch + blob rather than open the URL directly).
 */
export function exportSupportAuditCsv(filters = {}) {
  const { accountId, engineerId, status, pageStart, pageEnd } = filters;
  const params = new URLSearchParams();
  params.set("format", "csv");
  if (accountId) params.set("accountId", accountId);
  if (engineerId) params.set("engineerId", engineerId);
  if (status && status !== "All") params.set("status", status);
  if (pageStart) params.set("pageStart", pageStart);
  if (pageEnd) params.set("pageEnd", pageEnd);
  return fetch(`${API_URL}/support-audit/export?${params.toString()}`, {
    headers: authHeaders(),
  }).then(async (res) => {
    if (res.ok) return res.text();
    const body = await res.text().catch(() => "");
    const err = new Error(`HTTP ${res.status}: ${body.slice(0, 200)}`);
    err.status = res.status;
    err.body = body;
    throw err;
  });
}
